import React from 'react';
import { Check } from 'lucide-react';
import { servicesData } from '../data/servicesData';
import { BRAND_COLORS, BRAND_FONTS } from '../constants/brandColors';
import { useCadToUsdRate } from '../hooks/useCadToUsdRate';
import PriceReveal from './PriceReveal';

// ⚙️ Feature flag: hide pricing until clientele adjusts.
// Set to true to reveal the "Découvrir le tarif" button + pricing panel again.
const SHOW_PRICING = true;

const ServicesSection = ({ language = 'fr', onOpenConsultationForm }) => {
  const data = servicesData[language];
  const cadToUsd = useCadToUsdRate();

  return (
    <section id="services" className="py-20" style={{ backgroundColor: BRAND_COLORS.lightCream }}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h2
            className="text-4xl sm:text-5xl font-light mb-6 italic"
            style={{ fontFamily: BRAND_FONTS.heading, color: BRAND_COLORS.darkWarm }}
          >
            {data.pageTitle}
          </h2>
          <div className="w-24 h-1 mx-auto mb-6" style={{ backgroundColor: BRAND_COLORS.gold }}></div>
          <p className="text-lg leading-relaxed" style={{ color: BRAND_COLORS.darkWarm, opacity: 0.7 }}>
            {data.intro}
          </p>
        </div>

        {/* Main Packages */}
        <div className="max-w-7xl mx-auto mb-20">
          <h3 
            className="text-3xl sm:text-4xl font-light mb-12 text-center italic"
            style={{ fontFamily: BRAND_FONTS.heading, color: BRAND_COLORS.darkWarm }}
          >
            {language === 'fr' ? 'Nos Formules Complètes' : 'Our Complete Packages'}
          </h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {data.packages.map((pkg, index) => {
              return (
                <div
                  key={pkg.id}
                  className="relative bg-white rounded-lg shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden group"
                >
                  {/* Most Popular Badge */}
                  {index === 1 && (
                    <div 
                      className="absolute top-4 right-4 text-white px-6 py-2 text-sm font-semibold tracking-wider z-10 rounded-md"
                      style={{ backgroundColor: BRAND_COLORS.bordeaux }}
                    >
                      {language === 'fr' ? '⭐ LA PLUS CHOISIE' : '⭐ MOST POPULAR'}
                    </div>
                  )}
                  
                  <div className="p-8">
                    {/* Package Number */}
                    <div 
                      className="text-sm uppercase tracking-widest mb-2 font-semibold"
                      style={{ color: BRAND_COLORS.gold }}
                    >
                      {pkg.number}
                    </div>
                  
                  {/* Package Name */}
                  <h4
                    className="text-3xl font-light mb-2 italic"
                    style={{ fontFamily: BRAND_FONTS.heading, color: BRAND_COLORS.darkWarm }}
                  >
                    {pkg.name}
                  </h4>
                  
                  {/* Duration */}
                  <p className="text-sm mb-4" style={{ color: BRAND_COLORS.darkWarm, opacity: 0.6 }}>{pkg.duration}</p>
                  
                  {/* Description */}
                  <p className="mb-6 leading-relaxed" style={{ color: BRAND_COLORS.darkWarm }}>
                    {pkg.description}
                  </p>

                  {/* For You If */}
                  {pkg.forYouIf && (
                    <div className="mb-6">
                      <h5 
                        className="text-sm uppercase tracking-wider font-semibold mb-3"
                        style={{ color: BRAND_COLORS.bordeaux }}
                      >
                        {language === 'fr' ? 'C\'EST POUR VOUS SI :' : 'THIS IS FOR YOU IF:'}
                      </h5>
                      <ul className="space-y-2">
                        {pkg.forYouIf.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: BRAND_COLORS.darkWarm }}>
                            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: BRAND_COLORS.bordeaux }} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Sessions */}
                  {pkg.sessions && (
                    <div className="mb-6">
                      <h5 
                        className="text-sm uppercase tracking-wider font-semibold mb-3"
                        style={{ color: BRAND_COLORS.bordeaux }}
                      >
                        {language === 'fr' ? 'DÉROULEMENT :' : 'SESSIONS:'}
                      </h5>
                      <ul className="space-y-2">
                        {pkg.sessions.map((session, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: BRAND_COLORS.darkWarm }}>
                            <span style={{ color: BRAND_COLORS.gold }} className="font-semibold">{idx + 1}.</span>
                            <span>{session}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Deliverables */}
                  {pkg.deliverables && (
                    <div className="mb-6 p-4 rounded-md" style={{ backgroundColor: BRAND_COLORS.cream }}>
                      <h5 
                        className="text-sm uppercase tracking-wider font-semibold mb-3"
                        style={{ color: BRAND_COLORS.bordeaux }}
                      >
                        {language === 'fr' ? 'VOUS REPARTEZ AVEC :' : 'YOU LEAVE WITH:'}
                      </h5>
                      <ul className="space-y-2">
                        {pkg.deliverables.map((item, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm" style={{ color: BRAND_COLORS.darkWarm }}>
                            <Check className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: BRAND_COLORS.bordeaux }} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Pricing — collapsible (hidden by feature flag while clientele adjusts) */}
                  <div className="mt-6">
                    {SHOW_PRICING && (
                      <PriceReveal
                        pricing={pkg.pricing}
                        language={language}
                        cadToUsd={cadToUsd}
                        variant="package"
                      />
                    )}

                    <button
                      onClick={() => onOpenConsultationForm(pkg.name)}
                      className="w-full mt-6 px-6 py-4 text-white rounded-md transition-all duration-200 uppercase tracking-wider text-sm font-bold shadow-md hover:shadow-lg cursor-pointer"
                      style={{ backgroundColor: BRAND_COLORS.bordeaux }}
                    >
                      {data.bookButton}
                    </button>
                  </div>
                </div>
              </div>
            );
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="max-w-4xl mx-auto mb-20">
          <div 
            className="w-32 h-1 mx-auto" 
            style={{ background: `linear-gradient(to right, transparent, ${BRAND_COLORS.gold}, transparent)` }}
          ></div>
        </div>

        {/* À La Carte Services */}
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h3 
              className="text-3xl sm:text-4xl font-light mb-4 italic"
              style={{ fontFamily: BRAND_FONTS.heading, color: BRAND_COLORS.darkWarm }}
            >
              {data.alaCarte.title}
            </h3>
            <p className="text-lg max-w-2xl mx-auto" style={{ color: BRAND_COLORS.darkWarm, opacity: 0.7 }}>
              {data.alaCarte.intro}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.alaCarte.services.map((service) => (
              <div
                key={service.id}
                className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden group"
              >
                <div className="p-6">
                {/* Service Name */}
                <h5
                  className="text-xl font-light mb-2 italic"
                  style={{ fontFamily: BRAND_FONTS.heading, color: BRAND_COLORS.darkWarm }}
                >
                  {service.name}
                </h5>
                
                {/* Duration & Note */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm" style={{ color: BRAND_COLORS.darkWarm, opacity: 0.6 }}>{service.duration}</span>
                  {service.note && (
                    <span 
                      className="text-xs px-2 py-1 rounded uppercase font-semibold"
                      style={{ backgroundColor: BRAND_COLORS.cream, color: BRAND_COLORS.bordeaux }}
                    >
                      {service.note}
                    </span>
                  )}
                </div>
                
                {/* Description */}
                <p className="text-sm mb-4 leading-relaxed" style={{ color: BRAND_COLORS.darkWarm }}>
                  {service.description}
                </p>
                
                {/* Deliverables */}
                <div 
                  className="mb-4 p-3 rounded text-xs"
                  style={{ backgroundColor: BRAND_COLORS.cream, color: BRAND_COLORS.darkWarm }}
                >
                  <strong>{language === 'fr' ? 'Vous repartez avec : ' : 'You leave with: '}</strong>
                  {service.deliverables}
                </div>

                {/* Pricing — collapsible (hidden by feature flag) */}
                {SHOW_PRICING && (
                  <PriceReveal
                    pricing={service.pricing}
                    language={language}
                    cadToUsd={cadToUsd}
                    variant="alacarte"
                  />
                )}

                {/* Booking CTA */}
                <button
                  onClick={() => onOpenConsultationForm(service.name)}
                  className="w-full mt-4 px-6 py-3 text-white rounded-md transition-all duration-200 uppercase tracking-wider text-xs font-bold shadow-md hover:shadow-lg cursor-pointer"
                  style={{ backgroundColor: BRAND_COLORS.bordeaux }}
                  data-testid={`alacarte-book-${service.id}`}
                >
                  {language === 'fr' ? 'Réserver ma consultation' : 'Book my consultation'}
                </button>
              </div>
            </div>
            ))}
          </div>
        </div>

        {/* Free Consultation CTA */}
        <div className="max-w-3xl mx-auto mt-20 text-center bg-white rounded-lg shadow-lg p-8">
          <h4
            className="text-2xl sm:text-3xl font-light mb-4 italic"
            style={{ fontFamily: BRAND_FONTS.heading, color: BRAND_COLORS.darkWarm }}
          >
            {data.freeConsultation}
          </h4>
          <p className="mb-6" style={{ color: BRAND_COLORS.darkWarm, opacity: 0.7 }}>
            {language === 'fr' 
              ? 'Un échange gratuit de 20 minutes pour faire connaissance et identifier vos besoins.'
              : 'A free 20-minute chat to get to know each other and identify your needs.'}
          </p>
          <button 
            onClick={() => onOpenConsultationForm('Consultation Découverte Offerte')}
            className="px-8 py-4 text-white rounded-md transition-all duration-200 uppercase tracking-wider text-sm font-bold shadow-md hover:shadow-xl cursor-pointer"
            style={{ backgroundColor: BRAND_COLORS.bordeaux }}
          >
            {data.bookFreeConsult}
          </button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
